/**
 * Retry utility with exponential backoff
 *
 * Retries async functions with exponential backoff for better error recovery
 * during network issues or temporary server problems.
 *
 * @param {Function} fn - Async function to retry
 * @param {Object} options - Retry configuration options
 * @param {number} options.maxRetries - Maximum number of retry attempts (default: 3)
 * @param {number} options.initialDelayMs - Initial delay in milliseconds (default: 1000)
 * @param {number} options.maxDelayMs - Maximum delay in milliseconds (default: 30000)
 * @param {number} options.backoffMultiplier - Multiplier for exponential backoff (default: 2)
 * @param {number[]} options.retryableStatusCodes - HTTP status codes that should trigger retry (default: [408, 429, 500, 502, 503, 504])
 * @returns {Promise} Result of the function or throws error after max retries
 */
export async function retryWithBackoff(fn, options = {}) {
  const {
    maxRetries = 3,
    initialDelayMs = 1000,
    maxDelayMs = 30000,
    backoffMultiplier = 2,
    retryableStatusCodes = [408, 429, 500, 502, 503, 504],
  } = options;

  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;

      // Don't retry if status code is not retryable
      if (status && !retryableStatusCodes.includes(status)) {
        throw error;
      }

      // Don't retry if error doesn't have a status code but is not a network error
      if (!status && error.code !== 'ECONNABORTED' && error.code !== 'ERR_NETWORK') {
        throw error;
      }

      // Don't delay after last attempt
      if (attempt < maxRetries) {
        const delay = Math.min(
          initialDelayMs * Math.pow(backoffMultiplier, attempt),
          maxDelayMs
        );
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}



